import {
  Event,
  PromoCode,
  PromoCodeStatus,
  TicketV2,
} from "@markit/common.types";
import { addAlpha, Colors } from "../../../utils/colors";
import { API } from "../../../API";
import React, { useMemo, useState } from "react";
import { useTheme } from "../../../hooks/useTheme";
import ThreeDotsButton from "../../Buttons/ThreeDotsButton";
import { HorizontalDivider } from "../../Dividers/HorizontalDivider";
import { DropdownMenu, DropdownMenuItem } from "../../DropdownMenu";
import useAsyncEffect from "../../../hooks/useAsyncEffect";
import { getPromoCodeDiscountLabel } from "../../../utils/eventUtils/customTicketUtils";
import LabelButton from "../../Buttons/LabelButton";

export const PROMO_FLEX_SECTIONS = [10, 5, 2];

type PromoCodeItemProps = {
  event: Event;
  item: PromoCode | TicketV2;
  index: number;
  totalItems: number;
  setItem: (promoCodeId: PromoCode | TicketV2) => void;
  setFreePromoRedeemed: (freePromoRedeemed: boolean) => void;
  freeTicketsTotalCount: number;
  getDropdownItems: (item: PromoCode | TicketV2) => DropdownMenuItem[];
};

export const PromoCodeItem = (props: PromoCodeItemProps) => {
  const {
    event,
    item,
    index,
    totalItems,
    setItem,
    setFreePromoRedeemed,
    freeTicketsTotalCount,
    getDropdownItems,
  } = props;
  const { theme } = useTheme();
  const [totalFreePromo, setTotalFreePromo] = useState(1);
  const [usedFreePromo, setUsedFreePromo] = useState(0);
  const [isLoading, setIsLoading] = useState(false);

  const isDiscountCode = useMemo(
    () => (item as PromoCode).type !== undefined,
    [item]
  );

  // Only discount promo codes carry a PromoCodeStatus - free ticket codes are
  // always actionable and never show as inactive.
  const isInactive = useMemo(
    () =>
      isDiscountCode && (item as PromoCode).status === PromoCodeStatus.INACTIVE,
    [isDiscountCode, item]
  );

  const dropdownItems = useMemo(
    () => getDropdownItems(item),
    [getDropdownItems, item]
  );

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useAsyncEffect(async () => {
    if ((item as TicketV2).alias) {
      await Promise.all([
        API.promoCodes
          .getTotalFreePromo({
            eventId: event.id,
            alias: item.alias,
          })
          .then(async (response) => {
            const { totalFreePromo } = response;
            setTotalFreePromo(totalFreePromo);
          }),
        API.promoCodes
          .getUsedFreePromo({
            eventId: event.id,
            alias: item.alias,
          })
          .then(async (response) => {
            const { usedFreePromo } = response;
            setUsedFreePromo(usedFreePromo);
          }),
      ]);
    }
    setIsLoading(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [event.id, item.id, item.alias, freeTicketsTotalCount]);

  return (
    <div key={item.id}>
      <div
        style={{
          display: "flex",
          paddingInline: 14,
          paddingBlock: 7,
          gap: 14,
        }}
      >
        <div
          className="AlignedRow"
          style={{ gap: 10, flex: PROMO_FLEX_SECTIONS[0] }}
        >
          <span
            style={{
              color: Colors.BLACK,
              fontSize: 14,
              fontWeight: 600,
            }}
            className="EventDashboardContentDescription"
          >
            {item.alias}
          </span>
          {isInactive ? (
            <LabelButton
              color={Colors.BLACK}
              backgroundColor={addAlpha(Colors.ORANGE1, 0.1)}
              borderColor={Colors.ORANGE1}
              text="Inactive"
            />
          ) : null}
        </div>
        <div style={{ flex: PROMO_FLEX_SECTIONS[1] }}>
          <span
            style={{
              color: theme.SubText.color,
              fontSize: 14,
              marginLeft: 20,
            }}
            className="EventDashboardContentDescription"
          >
            {isDiscountCode
              ? getPromoCodeDiscountLabel(item as PromoCode)
              : "Free"}
          </span>
        </div>
        <div
          className="AlignedRowSpaced"
          style={{ flex: PROMO_FLEX_SECTIONS[2] }}
        >
          <span
            style={{
              paddingLeft: 24,
              color: theme.SubText.color,
            }}
            className="EventDashboardContentDescription"
          >
            {isDiscountCode
              ? (item as PromoCode).maxUseCount !== undefined
                ? `${(item as PromoCode).usedCount ?? 0}/${
                    (item as PromoCode).maxUseCount
                  }`
                : "-/-"
              : !isLoading
                ? usedFreePromo + "/" + totalFreePromo
                : "-/-"}
          </span>
          <div>
            <DropdownMenu
              dropdownItems={dropdownItems}
              trigger={["click"]}
              placement="bottomLeft"
              altWidth={150}
            >
              <ThreeDotsButton
                onPress={() => {
                  if (!isDiscountCode) {
                    setFreePromoRedeemed(usedFreePromo > 0);
                  }
                  setItem(item);
                }}
              />
            </DropdownMenu>
          </div>
        </div>
      </div>
      {index !== totalItems - 1 && <HorizontalDivider altMargin={14} />}
    </div>
  );
};
